import { MediumFormatting } from "@faire/web/common/localization/formatting";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";

import { useFirstOrderIncentiveFormattedData } from "@faire/retailer/hooks/firstOrderIncentive/useFirstOrderIncentiveFormattedData";
import { useFirstOrderIncentiveStore } from "@faire/retailer/hooks/firstOrderIncentive/useFirstOrderIncentiveStore";
import { useFirstOrderIncentiveTimeRemaining } from "@faire/retailer/hooks/firstOrderIncentive/useFirstOrderIncentiveTimeRemaining";

import { useVerticalizedFOITopBannerText } from "./useVerticalizedFOITopBannerText";

export const useGenericText = () => {
  const {
    formattedIncentiveData,
    isMaximalistFoi,
    isFirstOrderForFaireDirectIncentive,
  } = useFirstOrderIncentiveStore();
  const verticalizedText = useVerticalizedFOITopBannerText();

  const { expiresAt } = formattedIncentiveData;

  const { hoursRemaining } = useFirstOrderIncentiveTimeRemaining(
    expiresAt ?? 0
  );

  const { formattedIncentivePercent } = useFirstOrderIncentiveFormattedData();

  const genericLessThan24HoursText = () => {
    if (isFirstOrderForFaireDirectIncentive) {
      return (
        <StrictLocalMsg
          defaultMessage="<MediumFormatting>Last day!</MediumFormatting> Enjoy {percentNumber} off with any brand."
          description={{
            text: "A banner at the top of the page telling the user that it's the last day to use their sign-up offer.",
          }}
          values={{
            MediumFormatting,
            percentNumber: formattedIncentivePercent,
          }}
        />
      );
    }

    if (isMaximalistFoi) {
      return (
        <StrictLocalMsg
          defaultMessage="<MediumFormatting>Last day!</MediumFormatting> Enjoy {percentNumber} off and free shipping."
          description={{
            text: "A banner at the top of the page telling the user that it's the last day to use their sign-up offer.",
          }}
          values={{
            MediumFormatting,
            percentNumber: formattedIncentivePercent,
          }}
        />
      );
    }

    return (
      <StrictLocalMsg
        defaultMessage="<MediumFormatting>Last day!</MediumFormatting> Enjoy {percentNumber} off."
        description={{
          text: "A banner at the top of the page telling the user that it's the last day to use their sign-up offer.",
        }}
        values={{
          MediumFormatting,
          percentNumber: formattedIncentivePercent,
        }}
      />
    );
  };

  const genericLessThan48HoursText = () => {
    if (isFirstOrderForFaireDirectIncentive) {
      return (
        <StrictLocalMsg
          defaultMessage="<MediumFormatting>2 days left!</MediumFormatting> Enjoy {percentNumber} off with any brand."
          description={{
            text: "A banner at the top of the page telling the user that they have 2 days left to use their sign-up offer.",
          }}
          values={{
            MediumFormatting,
            percentNumber: formattedIncentivePercent,
          }}
        />
      );
    }

    if (isMaximalistFoi) {
      return (
        <StrictLocalMsg
          defaultMessage="<MediumFormatting>2 days left!</MediumFormatting> Enjoy {percentNumber} off and free shipping."
          description={{
            text: "A banner at the top of the page telling the user that they have 2 days left to use their sign-up offer.",
          }}
          values={{
            MediumFormatting,
            percentNumber: formattedIncentivePercent,
          }}
        />
      );
    }

    return (
      <StrictLocalMsg
        defaultMessage="<MediumFormatting>2 days left!</MediumFormatting> Enjoy {percentNumber} off."
        description={{
          text: "A banner at the top of the page telling the user that they have 2 days left to use their sign-up offer.",
        }}
        values={{
          MediumFormatting,
          percentNumber: formattedIncentivePercent,
        }}
      />
    );
  };

  const genericText = () => {
    switch (true) {
      case hoursRemaining < 24:
        return genericLessThan24HoursText();
      case hoursRemaining < 48:
        return genericLessThan48HoursText();
      default:
        return verticalizedText;
    }
  };

  return genericText();
};
